import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { sanitizeRichtextHtml } from '../../sanitize/sanitizeRichtextHtml.js';
import { ALIGN_OPTIONS, marginFields, visibilityField } from '../_shared/blockFields.js';
import { marginStyle, str, visibilityCss } from '../_shared/blockRender.js';
import { useProductState } from '../_shared/productContext.js';
/**
 * Shrine `emoji-benefits` (preset "Emoji benefits", category "Information display") — config-by-config.
 *
 * Section-specific shape: a compact list of benefit lines, each an emoji + short text
 * (e.g. "🚚 Livraison rapide"). Shrine stores the whole list as ONE `benefits` richtext snippet whose
 * default is three emoji bullet `<p>` lines — so each `<p>` in the richtext is one benefit line. That
 * exact authoring surface is reproduced here (the `benefits` richtext, rendered line-per-`<p>`).
 *
 * SHRINE INVENTORY (3 settings — every one implemented + wired):
 *  - `benefits` (richtext, root, label "Benefits") → our `benefits` richtext setting, default = the same
 *    3 emoji bullet `<p>` lines (Shrine's source had the 2nd/3rd emoji bytes mangled to replacement
 *    glyphs; we restore real, useful emoji defaults — 🚚 / 🔒 / ⭐ — so the default render is meaningful).
 *  - `margin_top` (range 0–45 step 3 px, def 15) → marginFields top.
 *  - `margin_bottom` (range 0–45 step 3 px, def 15) → marginFields bottom.
 * Non-setting Shrine schema entries (NOT settings): the "Margins" header, and the presets block — both
 * reproduced structurally via the Margins group + meta label, not as authoring controls.
 *
 * Section-specific brief additions (control the SAME single `benefits` list, no new Shrine settings
 * invented beyond the explicit "honor layout (rows/inline), alignment" ask):
 *  - `layout` (rows / inline) — stack the lines vertically (rows) or flow them inline-wrapping.
 *  - `alignment` + `mobileAlignment` — left/center/right, responsive at Shrine's 750px breakpoint via BOTH
 *    `@media` (live viewport) and `@container aqx-page` (builder device-preview), like the text/title blocks.
 *
 * This is a product buy-box block: it reads the shared product state and renders nothing outside a
 * product container (Shrine drops it in the product information column).
 *
 * DEFERRED (Shopify-platform-only, render the control where one exists; reason each):
 *  - `richtext` field TYPE is Shopify's theme-editor rich-text input — we DO implement the setting (our
 *    `richtext` field provides the equivalent authoring control); nothing else to defer here.
 *  - `block.shopify_attributes` — a Liquid template attribute injected by Shopify's section renderer, not a
 *    schema setting; no equivalent in our block tree.
 *  - preset category "Information display" — a Shopify section/block taxonomy label, surfaced via meta, not
 *    an authoring setting.
 * (No animation / glyph-font / CDN / liquid-app / cart-AJAX settings exist on this Shrine block, so there
 * are none to defer.)
 */
export const emojiBenefitsBlockSchema = {
    type: 'emoji-benefits',
    name: 'Emoji benefits',
    fields: [
        visibilityField,
        {
            id: 'benefits',
            type: 'richtext',
            label: 'Benefits',
            info: 'One benefit per line — start each line with an emoji (e.g. "🚚 Livraison rapide").',
            default: {
                format: 'html',
                html: '<p>🚚 Livraison rapide</p><p>🔒 Paiement sécurisé</p><p>⭐ Qualité garantie</p>',
            },
        },
        {
            id: 'layout',
            aiConfigurable: true,
            type: 'select',
            label: 'Layout',
            group: 'General',
            info: 'Stack each benefit on its own row, or flow them inline (wrapping).',
            default: 'rows',
            options: [
                { value: 'rows', label: 'Rows' },
                { value: 'inline', label: 'Inline' },
            ],
        },
        {
            id: 'alignment',
            aiConfigurable: true,
            type: 'select',
            label: 'Desktop alignment',
            group: 'General',
            default: 'left',
            options: ALIGN_OPTIONS,
        },
        {
            id: 'mobileAlignment',
            type: 'select',
            label: 'Mobile alignment',
            group: 'Mobile',
            default: 'left',
            options: ALIGN_OPTIONS,
        },
        ...marginFields(15, 15),
    ],
};
/** Map a Shrine alignment value to the matching flex `justify-content` keyword. */
function justify(align) {
    if (align === 'center')
        return 'center';
    if (align === 'right')
        return 'flex-end';
    return 'flex-start';
}
/**
 * A compact list of benefit lines. The body is the block's OWN sanitized `benefits` richtext — each `<p>`
 * is one benefit line (emoji + short text). `layout` switches the container between a vertical column
 * (rows) and an inline-wrapping flow; `alignment`/`mobileAlignment` are responsive at 750px in both
 * `@media` (live viewport) and `@container aqx-page` (builder device-preview).
 */
const EmojiBenefitsBlock = ({ block }) => {
    const ps = useProductState();
    if (!ps)
        return null;
    const bs = block.settings;
    const cls = `aqx-emb-${block.id}`;
    const rt = bs.benefits;
    const html = sanitizeRichtextHtml(rt?.format === 'html' ? rt.html : '');
    const layout = str(bs.layout, 'rows');
    const inline = layout === 'inline';
    const align = str(bs.alignment, 'left');
    const mAlign = str(bs.mobileAlignment, 'left');
    // Each benefit line is a `<p>`. Lay them out as flex items: column for rows, wrapping row for inline.
    // Horizontal alignment maps to a different flex axis per layout: for the inline (row-direction) layout it
    // is the MAIN axis → `justify-content`; for the default rows (column-direction) layout it is the CROSS
    // axis → `align-items` (justify-content would only move the auto-height column vertically = no effect).
    // Mobile-first base = mobile alignment; desktop overrides ≥750px via the same property.
    const alignProp = inline ? 'justify-content' : 'align-items';
    const listBase = `.${cls}{display:flex;flex-direction:${inline ? 'row' : 'column'};` +
        `flex-wrap:${inline ? 'wrap' : 'nowrap'};` +
        `gap:${inline ? 'var(--aqx-theme-space-2, 8px) var(--aqx-theme-space-4, 16px)' : 'var(--aqx-theme-space-2, 8px)'};` +
        `${alignProp}:${justify(mAlign)}}`;
    // Each `<p>` is reset to a tight, inline-flex benefit line (no default paragraph margins).
    const lineRule = `.${cls} p{margin:0;display:inline-flex;align-items:center;` +
        `gap:var(--aqx-theme-space-1, 4px);` +
        `font-size:var(--aqx-theme-text-sm, 14px);line-height:var(--aqx-theme-lh-snug, 1.4);` +
        `color:var(--aqx-scheme-text)}`;
    const desktopRule = `.${cls}{${alignProp}:${justify(align)}}`;
    const css = listBase +
        lineRule +
        // Desktop override via `@container aqx-page` ONLY (always present via PageRenderer) — a paired
        // @media(min-width) would fire off the builder's wide editor window inside a narrow mobile preview.
        `@container aqx-page (min-width:750px){${desktopRule}}` +
        visibilityCss(cls, bs.visibility);
    const style = {
        fontFamily: 'var(--aqx-theme-font)',
        wordBreak: 'break-word',
        ...marginStyle(bs, 15, 15),
    };
    return (_jsxs(_Fragment, { children: [_jsx("style", { dangerouslySetInnerHTML: { __html: css } }), _jsx("div", { className: cls, style: style, dangerouslySetInnerHTML: { __html: html } })] }));
};
export const emojiBenefitsBlock = {
    type: 'emoji-benefits',
    schema: emojiBenefitsBlockSchema,
    Component: EmojiBenefitsBlock,
    meta: { label: 'Emoji benefits' },
};
//# sourceMappingURL=index.js.map